import { useState, useEffect } from 'react';
import config from 'config';
import { getContrastForegroundColor } from 'web-common/scripts/utils/color';
import FeatureToggleUtils from 'FeatureToggleUtils';
import * as ApiUtils from '../views/reactViews/ApiUtil';
import { generateGoogleFontsLink } from '@bizzabo/fonts';

const useApplyCustomizations = eventId => {
    const [styleTextContent, setStyleTextContent] = useState('');

    useCustomizationEffect(() => {
        const controller = new AbortController();
        window.addEventListener(
            'scroll',
            () => {
                const rootStyles = getComputedStyle(document.documentElement);
                const applyGlassEffectToAgendaDaySelector = rootStyles.getPropertyValue('--apply-glass-effect-to-agenda-day-selector');
                const backdropFilterValue = rootStyles.getPropertyValue('--agenda-day-selector-backdrop-filter');
                if (!applyGlassEffectToAgendaDaySelector) {
                    return;
                }
                if (window.pageYOffset >= 20) {
                    if (backdropFilterValue !== 'blur(30px)') {
                        document.documentElement.style.setProperty('--agenda-day-selector-backdrop-filter', 'blur(30px)');
                        document.documentElement.style.setProperty('--agenda-day-selector-background-color', 'rgba(255, 255, 255, 0.05)');
                    }
                } else {
                    document.documentElement.style.setProperty('--agenda-day-selector-backdrop-filter', 'none');
                    document.documentElement.style.setProperty('--agenda-day-selector-background-color', 'transparent');
                }
            },
            { signal: controller.signal }
        );
        return () => controller.abort();
    }, []);

    useCustomizationEffect(() => {
        const style = getCustomizationsStyleElement();

        const applyCustomizations = async() => {
            try {
                const customizations = await ApiUtils.getAgendaCustomization(eventId);
                const newStyleTextContent = customizationsToCss(customizations);
                style.textContent = newStyleTextContent;
                setStyleTextContent(newStyleTextContent);
            } catch (e) {
                console.log(e);
            }
        };
        applyCustomizations();
    }, []);

    useCustomizationEffect(() => {
        const style = getCustomizationsStyleElement();
        const controller = new AbortController();

        window.addEventListener(
            'message',
            ({ origin, data }) => {
                if (origin !== config.dashboardUrl) {
                    return;
                }
                const { action, customizations } = data;
                if (action === 'preview') {
                    const newStyleTextContent = customizationsToCss(customizations);
                    style.textContent = newStyleTextContent;
                    return;
                }
                if (action === 'save') {
                    const newStyleTextContent = customizationsToCss(customizations);
                    style.textContent = newStyleTextContent;
                    setStyleTextContent(newStyleTextContent);
                    return;
                }
                if (action === 'reset') {
                    style.textContent = styleTextContent;
                    return;
                }
            },
            { signal: controller.signal }
        );
        return () => controller.abort();
    }, [styleTextContent]);
};

const useCustomizationEffect = (effect, dependencyArray) => {
    useEffect(() => {
        const isAgendaCustomizationCornerStyleFeatureEnabled = FeatureToggleUtils.isFeatureEnabled(
            'agenda-customization-corner-style'
        );
        if (!isAgendaCustomizationCornerStyleFeatureEnabled) {
            return;
        }
        return effect();
    }, dependencyArray);
};

const getCustomizationsStyleElement = () => {
    const STYLE_TAG_ID = 'customizations';
    let style = document.getElementById(STYLE_TAG_ID);
    if (!style) {
        style = document.createElement('style');
        style.id = STYLE_TAG_ID;
    }
    document.head.insertBefore(style, document.head.firstChild);
    return style;
};

const makeGeneralCss = ({
    cornerStyle,
    pageBackground,
    pageBackgroundImage,
    pageBackgroundImagePosition,
    pageBackgroundImageRepeat,
    pageBackgroundImageSize,
    pageBackgroundImageAttachment,
}) => {
    let generalCss = '';
    if (cornerStyle === 'rounded') {
        generalCss += `
--border-radius-xs: 6px;
--border-radius-s: 8px;
--border-radius-m: 12px;
--border-radius-l: 16px;
--border-radius-xl: 20px;
--border-radius-xxl: 30px;
--box-shadow-none: none;
--hide-title-indicator: none;
.agenda-view:has(.session-page) {--agenda-session-page-mobile-padding: 16px;}\n`;
    }

    if (pageBackground === 'image' && pageBackgroundImage) {
        generalCss += `--page-background: url('${pageBackgroundImage}') ${pageBackgroundImageAttachment} ${pageBackgroundImageRepeat} ${pageBackgroundImagePosition}/${pageBackgroundImageSize};\n`;
        generalCss += '--apply-glass-effect-to-agenda-day-selector: true;\n';
        generalCss += '--agenda-day-selector-background-color: transparent;\n';
        generalCss += '--agenda-day-selector-backdrop-filter: none;\n';
    } else {
        generalCss += '--agenda-day-selector-background-color: var(--page-background-color);\n';
        generalCss += '--agenda-day-selector-backdrop-filter: none;\n';
    }
    return generalCss;
};

const makeColorsCss = colors => {
    const colorsCss = Object.entries(colors).reduce((css, [colorName, { r, g, b, a = 1 }]) => {
        if (r === undefined || g === undefined || b === undefined) {
            return css;
        }
        if (colorName === 'pageBackgroundColor') {
            css += `
--page-background-transparent-color: transparent;
--page-background-color-opacity-80: rgba(${r} ${g} ${b} / 0.8);\n`;
        }
        if (colorName === 'pageMainColor') {
            const contrastToMainColor = getContrastForegroundColor({ r, g, b });
            css += `
--page-contrast-to-main-color: ${contrastToMainColor};
--page-main-light-color: rgba(${r} ${g} ${b} / 0.7);
--page-main-color-opacity-0: transparent;
--page-main-color-opacity-10: rgba(${r} ${g} ${b} / 0.1);
--page-main-color-opacity-40: rgba(${r} ${g} ${b} / 0.4);
--page-dropdown-color-1: #595a5c;
--page-dropdown-color-2: #76778C;
--page-dropdown-color-3: #F5F5F7;
.speaker-back-button {--color-5: ${contrastToMainColor}};
.speaker-share-button {--color-5-background: var(--page-main-color);}
a.send-button.text-color-white {color: ${contrastToMainColor};}
a.cancel-button {color: black;}
.interactive-states-button__icon.color-1,
.interactive-states-button__text.color-1 {--page-main-color: #333;}
.popover-content .icon-AgendaAtt_heart.color-1,
.popover-content .icon-AgendaAtt_love {--page-main-color: #595a5c;}
.bz-hoc-filter {--page-main-color: var(--page-dropdown-color-2);}
.select-ticket-section {--page-main-color: var(--page-dropdown-color-2);}
.agenda-share-pop-over .success-message {--page-main-color: #595a5c;}
.third-party-cookie-modal a {--page-main-color: #0061f8;}
.register-login-modal-content a {--page-main-color: var(--page-dropdown-color-2);}
.register-login-modal-content a:hover {--page-main-color-opacity-10: var(--page-dropdown-color-3); color: var(--page-dropdown-color-2, #ffffff);}
.need-login-popover .text-color-white,
.need-login-popover .popover-content .popover-buttons .text-color-white:hover {color: var(--page-contrast-to-main-color, #ffffff);}\n`;
        }
        if (colorName === 'pageTextColor') {
            css += `
--page-separator-color: rgba(${r} ${g} ${b} / 0.15);
.tab-button-border.background-color-gray {--page-text-color: var(--page-separator-color);}\n`;
        }
        if (colorName === 'cardsFillColor') {
            css += `
--cards-backdrop-blur: 10px;
.preview-window-container .session-page {--cards-fill-color: transparent;}\n`;
        }
        if (colorName === 'cardsTextColor') {
            css += `
--cards-separator-color: rgba(${r} ${g} ${b} / 0.15);
--cards-text-light-color: rgba(${r} ${g} ${b} / 0.7);
--cards-text-color-opacity-0: rgba(${r} ${g} ${b} / 0);
--cards-text-color-opacity-25: rgba(${r} ${g} ${b} / 0.25);
.agenda-search {--cards-text-light-color: #76778C;}
.agenda-search .session-info-text {--cards-text-color: #76778C;}
.speaker-view .tweets-activity-title {color: var(--cards-text-color, inherit);}\n`;
        }
        const cssVariableName = '--' + colorName.replace(/([a-z])([A-Z])/g, '$1-$2').toLocaleLowerCase(); // pageBackgroundColor -> --page-background-color
        const cssVariableValue = `rgba(${r} ${g} ${b} / ${a})`;
        const cssVariable = `${cssVariableName}: ${cssVariableValue};\n`;
        return (css += cssVariable);
    }, '');
    return colorsCss;
};

const makeFontsCss = fonts => {
    if (!fonts) return '';
    let variables =
        `--title-font-family: ${fonts?.title?.font?.cssName};
        --title-font-weight: ${fonts?.title?.bold ? '700' : '400'};
        --title-font-style: ${fonts?.title?.italic ? 'italic' : 'normal'};
        --paragraph-font-family: ${fonts?.paragraph?.font?.cssName};
        --paragraph-font-weight: ${fonts?.paragraph?.bold ? '700' : '400'};
        --paragraph-font-style: ${fonts?.paragraph?.italic ? 'italic' : 'normal'};
        --popup-style-unset: 'unset';
        --popup-style-auto: 'auto';
        --popup-min-height: 115px;
        --tags-clear-nowrap: nowrap;
        `;
    if (fonts.size === 'larger') {
        [14, 16, 18, 24, 26, 28].forEach(fs => {
            variables = variables + `\n--font-size-${fs}: ${fs}px;`;
        });
        [16, 20, 24, 30, 31, 36].forEach(lh => {
            variables = variables + `\n--line-height-${lh}: ${lh}px;`;
        });
    }
    return variables;
};
const generateCustomFontRule = (font) => {
    font.configs.forEach((config) => {
        const fontFace = new FontFace(
            font.displayName,
            `url(${config.url})`,
            { weight: config.fontWeight, style: config.fontStyle }
        );
        document.fonts.add(fontFace);
    });
};

const loadGoogleFont = (fontsLink) => {
    const link = document.createElement('link');
    link.href = fontsLink;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
};

const sortFontByType = (font, googleFonts) => {
    if (!font) return;
    if (font.type === 'google') {
        font.family = font.displayName;
        googleFonts.push(font);
    } else generateCustomFontRule(font);
};

const getFontsRules = (fonts) => {
    const googleFonts = [];
    sortFontByType(fonts?.title?.font, googleFonts);
    sortFontByType(fonts?.paragraph?.font, googleFonts);
    const googleFontsLink = googleFonts.length ? generateGoogleFontsLink(googleFonts) : '';
    return googleFontsLink;
};

const customizationsToCss = (customizations = {}) => {
    const { general = {}, colors = {}, fontStyle } = customizations;
    const generalCss = makeGeneralCss(general);
    const colorsCss = makeColorsCss(colors);
    const fontsCss = makeFontsCss(fontStyle);
    const googleFontsLink = getFontsRules(fontStyle);
    if (googleFontsLink) loadGoogleFont(googleFontsLink);
    return `:root {\n${colorsCss}\n${generalCss}\n${fontsCss}\n}`;
};

export default useApplyCustomizations;
